import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import dayjs, { Dayjs } from 'dayjs';
import { DATE_TIME_FORMAT_DEFAUT, TIME_FORMAT_DEFAULT } from 'Config';
import { FaEllipsisV } from 'react-icons/fa';
import ReactTooltip from 'react-tooltip';
import styles from './CalendarSidePanel.module.scss';
import { VehicleReservationDto } from 'api/vehicleReservations/models/VehicleReservationDto';
import Badge from 'common/components/badge/Badge';
import Popover from 'common/components/popover/Popover';
import DropdownItem from 'common/components/popover/PopoverItem';
import { getReservationStatusBadgePreset, getReservationStatusLabel } from 'common/utils/reservationStatus';
import { CalendarMode } from './CalendarView';

type Props = {
    mode: CalendarMode;
    selectedDate: Dayjs | null;
    items: VehicleReservationDto[];
};

const CalendarSidePanel: React.FC<Props> = ({ mode, selectedDate, items }) => {
    const { t } = useTranslation();
    const history = useHistory();
    const [openActionsDropdown, setOpenActionsDropdown] = useState<string | null>(null);

    const { reservations, requests } = useMemo(() => {
        const res: VehicleReservationDto[] = [];
        const req: VehicleReservationDto[] = [];
        items.forEach(item => {
            if (item.status === 'PENDING_APPROVAL') {
                req.push(item);
            } else {
                res.push(item);
            }
        });
        return { reservations: res, requests: req };
    }, [items]);

    if (!selectedDate) {
        return (
            <div className={styles.panel}>
                <div className={styles.emptyState}>
                    {t('reservations.calendar.select_day')}
                </div>
            </div>
        );
    }

    const dateTitle = selectedDate.format('ddd, D MMMM YYYY');

    const navigateToDetails = (item: VehicleReservationDto, isRequest: boolean) => {
        const path = isRequest
            ? `/reservations/request-details/${item.id}?from=calendars`
            : `/reservations/details/${item.id}?from=calendars`;
        history.push(path);
    };

    const renderCard = (item: VehicleReservationDto, isRequest: boolean) => {
        const id = item.id || '';
        const regNumber = item.vehicleRegistrationNumber || item.requestedVehicleRegistrationNumber || '-';
        const brandName = item.vehicleBrandName || item.requestedVehicleBrandName || '';
        const modelName = item.vehicleModelName || item.requestedVehicleModelName || '';
        const tooltipText = [
            item.vehicleSegmentName ? `${t('vehicle.type')}: ${item.vehicleSegmentName}` : null,
            item.vehicleLocalNames ? `${t('vehicle.local')}: ${item.vehicleLocalNames}` : null,
        ].filter(Boolean).join('<br/>');
        const startDayjs = item.startDate ? dayjs(item.startDate) : null;
        const endDayjs = item.endDate ? dayjs(item.endDate) : null;
        const selectedDayStr = selectedDate ? selectedDate.format('YYYY-MM-DD') : '';
        const startsOnSelectedDay = startDayjs ? startDayjs.format('YYYY-MM-DD') === selectedDayStr : true;
        const endsOnSelectedDay = endDayjs ? endDayjs.format('YYYY-MM-DD') === selectedDayStr : true;
        const isMultiDay = !startsOnSelectedDay || !endsOnSelectedDay;

        const startTime = startDayjs
            ? startDayjs.format(isMultiDay ? DATE_TIME_FORMAT_DEFAUT : TIME_FORMAT_DEFAULT)
            : '';
        const endTime = endDayjs
            ? endDayjs.format(isMultiDay ? DATE_TIME_FORMAT_DEFAUT : TIME_FORMAT_DEFAULT)
            : '';
        const elem = document.getElementById('root');

        return (
            <div
                key={id}
                className={`${styles.card} ${isRequest ? styles.cardRequest : ''}`}
                onClick={() => navigateToDetails(item, isRequest)}
            >
                <div className={styles.cardContent}>
                    <div className={styles.cardInfo}>
                           <div className={styles.cardMeta} data-tip={tooltipText || t('reservations.list.vehicle')} data-html={!!tooltipText} data-for={`card-vehicle-${id}`}>
                            <span className={styles.cardRegistration}>{regNumber}</span>
                            {(brandName || modelName) && (
                                <>
                                    <span> - </span>
                                    <span>{brandName}{brandName && modelName ? ' ' : ''}{modelName}</span>
                                </>
                            )}
                            <ReactTooltip id={`card-vehicle-${id}`} />
                        </div>
                        {item.driverName && (
                            <div className={styles.cardDriver} data-tip={t('reservations.list.driver')} data-for={`card-driver-${id}`}>
                                {item.driverName}
                                <ReactTooltip id={`card-driver-${id}`} />
                            </div>
                        )}
                     
                        {startTime && (
                            <div className={styles.cardMeta} style={{ marginTop: 4 }} data-tip={`${t('reservations.list.date_start')} – ${t('reservations.list.date_end')}`} data-for={`card-time-${id}`}>
                                <span>{startTime} – {endTime}</span>
                                <ReactTooltip id={`card-time-${id}`} />
                            </div>
                        )}
                        {isRequest && item.status && (
                            <div className={styles.cardStatusBadge} data-tip={t('reservations.list.status')} data-for={`card-status-${id}`}>
                                <Badge
                                    text={getReservationStatusLabel(item.status, t)}
                                    preset={getReservationStatusBadgePreset(item.status)}
                                />
                                <ReactTooltip id={`card-status-${id}`} />
                            </div>
                        )}
                        {item.reason && (<>
                            <div className={styles.cardReason} data-tip={t('reservations.destination_reason')} data-for={`card-reason-${id}`}>
                                {item.reason}
                            </div>
                            <ReactTooltip id={`card-reason-${id}`} />
                        </>

                        )}
                    </div>
                    <div className={styles.cardActions} onClick={e => e.stopPropagation()}>
                        <Popover
                            positions={['left', 'bottom']}
                            align={'start'}
                            reposition
                            containerParent={elem ? elem : document.getElementsByTagName('body')[0]}
                            isOpen={openActionsDropdown === id}
                            onClickOutside={() => setOpenActionsDropdown(null)}
                            content={() =>
                                <div onClick={e => e.stopPropagation()}>
                                    <DropdownItem onClick={() => {
                                        setOpenActionsDropdown(null);
                                        navigateToDetails(item, isRequest);
                                    }}>
                                        {t('common.details')}
                                    </DropdownItem>
                                </div>
                            }
                        >
                            {() => (
                                <div
                                    className={styles.cardActionsButton}
                                    onClick={e => {
                                        e.stopPropagation();
                                        setOpenActionsDropdown(openActionsDropdown === id ? null : id);
                                    }}
                                >
                                    <FaEllipsisV />
                                </div>
                            )}
                        </Popover>
                    </div>
                </div>
                {tooltipText && <ReactTooltip id={`card-info-${id}`} html={true} />}
            </div>
        );
    };

    return (
        <div className={styles.panel}>
            <div className={styles.panelHeader}>
                <div className={styles.panelTitle}>{dateTitle}</div>
                <div className={styles.panelCounts}>
                    {(mode === 'reservations' || mode === 'combined') && reservations.length > 0 && (
                        <span className={`${styles.panelCountBadge} ${styles.panelCountReservations}`}>
                            {t('reservations.calendar.reservations_count', { count: reservations.length })}
                        </span>
                    )}
                    {(mode === 'requests' || mode === 'combined') && requests.length > 0 && (
                        <span className={`${styles.panelCountBadge} ${styles.panelCountRequests}`}>
                            {t('reservations.calendar.requests_count', { count: requests.length })}
                        </span>
                    )}
                </div>
            </div>

            <div className={styles.panelBody}>
                {items.length === 0 && (
                    <div className={styles.emptyState}>
                        {mode === 'reservations'
                            ? t('reservations.calendar.no_reservations')
                            : mode === 'requests'
                                ? t('reservations.calendar.no_requests')
                                : t('reservations.calendar.no_reservations')
                        }
                    </div>
                )}

                {reservations.map(item => renderCard(item, false))}
                {requests.map(item => renderCard(item, true))}
            </div>
        </div>
    );
};

export default CalendarSidePanel;
